import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  FilterCriteria,
  FilterOperator,
  FilterService,
  SavedFilterConfig,
} from "../../services/common/filter.service";

@Component({
  selector: "app-advanced-filter-panel",
  templateUrl: "./advanced-filter-panel.component.html",
})
export class AdvancedFilterPanelComponent implements OnInit {
  @Input() storageKey: string = "";
  @Input() columns: Map<string, string> = new Map();
  @Input() hideColumns: string[] = ["actions"];

  @Output() filtersChanged = new EventEmitter<FilterCriteria[]>();

  filterForm: FormGroup;
  savedFilterForm: FormGroup;
  availableOperators: FilterOperator[] = [];

  activeFilters: FilterCriteria[] = [];
  savedFilters: SavedFilterConfig[] = [];

  savedFiltersStorageKey: string = "";

  showSavedFiltersPanel = false;
  displayedColumns: string[] = [];

  constructor(private fb: FormBuilder, private filterService: FilterService) {
    this.filterForm = this.fb.group({
      column: [""],
      operator: ["contains"],
      value: [""],
    });

    this.savedFilterForm = this.fb.group({
      filterName: [""],
    });
  }

  ngOnInit(): void {
    this.availableOperators = this.filterService.availableOperators;

    // Convert column map to array for select options
    this.displayedColumns = Array.from(this.columns.keys()).filter(
      (column) => !this.hideColumns.includes(column)
    );

    if (this.displayedColumns.length > 0) {
      this.filterForm.patchValue({ column: this.displayedColumns[0] });
    }

    // Ensure storage key uniqueness to prevent filter sharing between components
    if (!this.storageKey) {
      console.warn("Advanced filter panel requires a unique storageKey");
      this.storageKey =
        "default-filter-" + Math.random().toString(36).substring(2, 9);
    }

    // Create a dedicated storage key for saved filters
    this.savedFiltersStorageKey = this.storageKey + "-saved";

    // Load filters from storage
    this.filterService.loadActiveFiltersFromStorage(this.storageKey);
    this.filterService.loadSavedFiltersFromStorage(this.savedFiltersStorageKey);

    // Subscribe to filter changes
    this.filterService
      .getActiveFilters(this.storageKey)
      .subscribe((filters) => {
        this.activeFilters = filters;
        this.filtersChanged.emit(filters);
      });

    this.filterService
      .getSavedFilters(this.savedFiltersStorageKey)
      .subscribe((savedFilters) => {
        this.savedFilters = savedFilters;
      });
  }

  getColumnDisplayName(columnKey: string): string {
    return this.columns.get(columnKey) || columnKey;
  }

  addFilter(): void {
    if (!this.filterForm.get("value")?.value) return;

    const filter: FilterCriteria = {
      column: this.filterForm.get("column")?.value,
      operator: this.filterForm.get("operator")?.value,
      value: this.filterForm.get("value")?.value,
    };

    this.filterService.addFilter(filter, this.storageKey);
    this.filterForm.patchValue({ value: "" });
  }

  removeFilter(index: number): void {
    this.filterService.removeFilter(index, this.storageKey);
  }

  clearAllFilters(): void {
    this.filterService.clearAllFilters(this.storageKey);
  }

  toggleSavedFiltersPanel(): void {
    this.showSavedFiltersPanel = !this.showSavedFiltersPanel;
  }

  saveCurrentFilters(): void {
    const filterName = this.savedFilterForm.get("filterName")?.value;
    if (!filterName || this.activeFilters.length === 0) return;

    if (this.savedFilters.some((f) => f.name === filterName)) {
      if (
        !confirm(
          `A filter configuration named "${filterName}" already exists. Do you want to replace it?`
        )
      ) {
        return;
      }
    }

    this.filterService.saveFilterConfig(
      filterName,
      this.activeFilters,
      this.savedFiltersStorageKey
    );

    this.savedFilterForm.patchValue({ filterName: "" });
  }

  loadFilterConfig(config: SavedFilterConfig): void {
    this.filterService.loadFilterConfig(config, this.storageKey);
  }

  deleteFilterConfig(index: number): void {
    if (confirm("Are you sure you want to delete this filter configuration?")) {
      this.filterService.deleteFilterConfig(index, this.savedFiltersStorageKey);
    }
  }
}
